/**
 * The memory book as a book you turn (PRO-4397). The pages are the API's preview sheets: the
 * front cover (square), one sheet per spread (2:1), and the back cover when the API has made it.
 *
 * A turning page is hinged in the spine and cut into STRIPS vertical strips, each nested in the
 * one before and turned a little more, so the page bends while it turns and its inner edge never
 * leaves the spine. CSS 3D driven by requestAnimationFrame; no library (StPageFlip rolled the page
 * off the spine along a diagonal, with a hard corner shadow).
 *
 * The book is flat 2D at rest: perspective and preserve-3d are set only while a page turns.
 * With them always on, desktop Chrome drew the resting cover distorted.
 *
 * Positions: 0 is the closed front cover. Two pages at a time (`single: false`), 1..N are the
 * spreads and N+1 the closed back cover. One page at a time (phones), every page is a position;
 * the left half of spread 1 (the endpaper, since page 1 is a right page) is skipped.
 */

export interface FlipSheets {
  back: string | null;
  cover: string;
  spreads: string[];
}

export interface BookFlip {
  destroy: () => void;
  /** Turn to a position, one page turn whatever the distance. */
  go: (to: number) => void;
  /** Straight to a position, without a turn. */
  jump: (to: number) => void;
  readonly last: number;
  readonly pos: number;
  /** New addresses for the same pages (the signed URLs are renewed). */
  setSheets: (sheets: FlipSheets) => void;
  /** The spread at the position: 0 the cover, 1..N, N+1 the back. */
  readonly spread: number;
  /** The position that shows a spread. */
  toPos: (spread: number) => number;
}

export interface FlipOptions {
  /** After every change of position, by any means. */
  onChange?: (pos: number, last: number, spread: number) => void;
  reducedMotion: () => boolean;
  sheets: FlipSheets;
  single: boolean;
}

/** A half of spread `s`, or a board: the cover (s = 0) or the back (s = N + 1). */
interface Face {
  s: number;
  side?: "L" | "R";
}

interface Turn {
  draw: (p: number) => void;
  end: (done: boolean) => void;
  fwd: boolean;
  start: number;
  target: number;
}

export const STRIPS = 12;
/** One page turn, ms. */
export const TURN_MS = { page: 750, spread: 1000 } as const;
/** How far the page bends at the middle of a turn, degrees; boards don't bend. */
const BEND = { page: 34, spread: 46 } as const;
/** Perspective in proportion to the book: a lifted page grows at most ~4 %, at any size. */
const PERSPECTIVE = 14;
/** A drag released before this share of the turn springs back. */
const COMMIT = 0.25;
const SPRING_MS = 450;
/** Pixels before a press becomes a drag. */
const SLOP = 8;

const ease = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

const url = (src: string) => `url("${src.replace(/"/g, "%22")}")`;

/** Phone: one page at a time. */
function facesOf(spreads: number, hasBack: boolean): Face[] {
  const faces: Face[] = [{ s: 0 }];
  for (let s = 1; s <= spreads; s += 1) {
    for (const side of ["L", "R"] as const) {
      if (!(s === 1 && side === "L")) {
        faces.push({ s, side });
      }
    }
  }
  if (hasBack) {
    faces.push({ s: spreads + 1 });
  }
  return faces;
}

export function createBookFlip(
  stage: HTMLElement,
  options: FlipOptions
): BookFlip {
  const { single, sheets: firstSheets } = options;
  let sheets = firstSheets;
  const n = sheets.spreads.length;
  const back = sheets.back ? n + 1 : -1;
  const faces = facesOf(n, back > 0);
  const last = single ? faces.length - 1 : Math.max(n, back);

  const area = document.createElement("div");
  area.className = "book-flip__area";
  const book = document.createElement("div");
  book.className = "book-flip__book";
  const left = document.createElement("div");
  left.className = "book-flip__page is-left is-empty";
  const right = document.createElement("div");
  right.className = "book-flip__page is-right";
  if (!single) {
    book.append(left);
  }
  book.append(right);
  area.append(book);
  stage.append(area);
  stage.classList.toggle("is-single", single);

  const reduced = () => options.reducedMotion();
  const threeD = (on: boolean) => {
    area.style.perspective = on
      ? `${Math.round(book.clientWidth * PERSPECTIVE)}px`
      : "none";
    book.classList.toggle("is-turning", on);
  };

  const isBoard = (d: Face) => d.s === 0 || d.s === back;
  const boardSrc = (d: Face) =>
    d.s === 0 ? sheets.cover : (sheets.back ?? "");
  const sheetSrc = (s: number) => sheets.spreads[s - 1] ?? "";
  const leftOf = (p: number): Face | null => {
    if (p === 0) {
      return null;
    }
    return p === back ? { s: back } : { s: p, side: "L" };
  };
  const rightOf = (p: number): Face | null => {
    if (p === back) {
      return null;
    }
    return p === 0 ? { s: 0 } : { s: p, side: "R" };
  };
  const face = (p: number) => faces[p] ?? null;

  /** A page slot shows a board whole, or one half of a spread's sheet. */
  const slotBg = (d: Face | null) => {
    if (!d) {
      return "none";
    }
    if (isBoard(d)) {
      return `${url(boardSrc(d))} center / 100% 100% no-repeat`;
    }
    return `${url(sheetSrc(d.s))} ${d.side === "L" ? "0%" : "100%"} 0 / 200% 100% no-repeat`;
  };
  /** One strip of a turning page: the slice x0..x0 + width of its face, in pixels. */
  const stripBg = (d: Face | null, x0: number, pw: number, h: number) => {
    if (!d) {
      return "var(--book-flip-paper)";
    }
    if (isBoard(d)) {
      return `${url(boardSrc(d))} ${-x0}px 0 / ${pw}px ${h}px no-repeat`;
    }
    const x = (d.side === "R" ? pw : 0) + x0;
    return `${url(sheetSrc(d.s))} ${-x}px 0 / ${2 * pw}px ${h}px no-repeat`;
  };

  const slots = { l: left, r: right };
  const shown: { l: Face | null; r: Face | null } = { l: null, r: null };
  function setSlot(which: "l" | "r", d: Face | null) {
    const el = slots[which];
    shown[which] = d;
    el.style.background = slotBg(d);
    el.classList.toggle("is-empty", !d);
    el.classList.toggle("is-board", !!d && isBoard(d));
  }

  /** A closed book is centred: the front board in the right half (−25 %), the back board in the left (+25 %). */
  const restX = (p: number) => {
    if (single) {
      return 0;
    }
    if (p === 0) {
      return -25;
    }
    return p === back ? 25 : 0;
  };
  const shift = (x: number) => {
    if (!single) {
      book.style.transform = x ? `translateX(${x}%)` : "";
    }
  };
  const spreadOf = (p: number) => (single ? (face(p)?.s ?? 0) : p);

  let pos = 0;
  let live: Turn | null = null;
  let frame = 0;

  function show(p: number) {
    if (single) {
      setSlot("r", face(p));
    } else {
      setSlot("l", leftOf(p));
      setSlot("r", rightOf(p));
    }
    shift(restX(p));
  }
  show(0);

  const changed = () => options.onChange?.(pos, last, spreadOf(pos));

  /** Sets up a turn from → to; `draw(p)` draws it at progress p (0 = before, 1 = after). */
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: one turn's set-up, drawing and landing share its state.
  function begin(from: number, to: number): Turn {
    const fwd = to > from;
    const opening = !single && (fwd ? from === 0 : to === 0);
    const closing = !single && (fwd ? to === back : from === back);
    const front = single ? face(fwd ? from : to) : rightOf(fwd ? from : to);
    const reverse = single ? null : leftOf(fwd ? to : from);
    // What the turning page uncovers is already in place underneath it.
    if (single) {
      if (fwd) {
        setSlot("r", face(to));
      }
    } else if (fwd) {
      setSlot("r", rightOf(to));
    } else {
      setSlot("l", leftOf(to));
    }
    const pw = book.clientWidth / (single ? 1 : 2);
    const h = book.clientHeight;
    const sw = pw / STRIPS;
    threeD(true);

    const leaf = document.createElement("div");
    leaf.className = "book-flip__leaf";
    leaf.style.left = `${single ? 0 : pw}px`;
    leaf.style.width = `${sw}px`;
    const strips: HTMLElement[] = [];
    const shades: [HTMLElement, HTMLElement][] = [];
    let parent: HTMLElement = leaf;
    for (let i = 0; i < STRIPS; i += 1) {
      const strip = document.createElement("div");
      strip.className = "book-flip__strip";
      // A hair of overlap so no seam shows between strips.
      strip.style.left = `${i === 0 ? 0 : sw - 0.5}px`;
      strip.style.width = `${sw + 1.5}px`;
      const fc = document.createElement("div");
      fc.className = "book-flip__front";
      fc.style.background = stripBg(front, i * sw, pw, h);
      const bc = document.createElement("div");
      bc.className = "book-flip__reverse";
      bc.style.background = stripBg(reverse, pw - (i + 1) * sw, pw, h);
      const fs = document.createElement("i");
      const bs = document.createElement("i");
      fs.className = "book-flip__shade";
      bs.className = "book-flip__shade";
      fc.append(fs);
      bc.append(bs);
      strip.append(fc, bc);
      parent.append(strip);
      parent = strip;
      strips.push(strip);
      shades.push([fs, bs]);
    }
    book.append(leaf);

    // The hard cover and back board turn without bending.
    let bend = 0;
    if (!(opening || closing)) {
      bend = single ? BEND.page : BEND.spread;
    }
    const shadeOf = (a: number, c: number) =>
      `linear-gradient(90deg, rgb(58 42 32 / ${a.toFixed(3)}), rgb(58 42 32 / ${c.toFixed(3)}))`;

    const draw = (p: number) => {
      const curve = bend * Math.sin(Math.PI * p);
      strips[0].style.transform = `rotateY(${-180 * p}deg)`;
      for (let i = 1; i < STRIPS; i += 1) {
        strips[i].style.transform =
          `rotateY(${(curve / (STRIPS - 1)) * (0.6 + 0.8 * (i / STRIPS))}deg)`;
      }
      // One continuous shade across the strips: each fades from its left value to its right one.
      const light = Math.sin(Math.PI * p);
      const k = (i: number) => 0.04 + 0.32 * light * (i / STRIPS) ** 1.3;
      shades.forEach(([fs, bs], i) => {
        fs.style.background = shadeOf(k(i), k(i + 1));
        bs.style.background = shadeOf(k(i + 1) * 0.8, k(i) * 0.8);
      });
      if (single) {
        leaf.style.opacity =
          p > 0.62 ? String(Math.max(0, 1 - (p - 0.62) / 0.3)) : "1";
      }
      // Opening and closing, the book slides between centred-closed and open.
      if (opening || closing) {
        const t = fwd ? p : 1 - p;
        shift(restX(from) + (restX(to) - restX(from)) * t);
      }
    };
    const end = (done: boolean) => {
      leaf.remove();
      threeD(false);
      // Landed: the turned page now lies on the other side. Sprung back: what was uncovered is
      // covered again.
      const at = done ? to : from;
      if (single) {
        setSlot("r", face(at));
      } else if (fwd === done) {
        setSlot("l", leftOf(at));
      } else {
        setSlot("r", rightOf(at));
      }
      shift(restX(at));
    };
    return { draw, end, fwd, start: fwd ? 0 : 1, target: fwd ? 1 : 0 };
  }

  function animate(
    turn: Turn,
    p0: number,
    p1: number,
    ms: number,
    done: () => void
  ) {
    if (reduced()) {
      turn.draw(p1);
      done();
      return;
    }
    const t0 = performance.now();
    const tick = (now: number) => {
      if (live !== turn) {
        return;
      }
      const t = Math.min(1, (now - t0) / ms);
      turn.draw(p0 + (p1 - p0) * ease(t));
      if (t < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        done();
      }
    };
    frame = requestAnimationFrame(tick);
  }

  /** A turn still running lands at once. */
  function settle() {
    if (live) {
      const turn = live;
      live = null;
      cancelAnimationFrame(frame);
      turn.end(true);
    }
  }

  function go(target: number) {
    const to = Math.max(0, Math.min(last, target));
    if (to === pos) {
      return;
    }
    settle();
    if (reduced()) {
      pos = to;
      show(pos);
      changed();
      return;
    }
    const from = pos;
    const turn = begin(from, to);
    live = turn;
    pos = to;
    changed();
    turn.draw(turn.start);
    animate(
      turn,
      turn.start,
      turn.target,
      single ? TURN_MS.page : TURN_MS.spread,
      () => {
        if (live === turn) {
          live = null;
          turn.end(true);
        }
      }
    );
  }

  function jump(target: number) {
    settle();
    pos = Math.max(0, Math.min(last, target));
    show(pos);
    changed();
  }

  // Drag a page to turn it; a tap on a page turns it.
  let drag: {
    from: number;
    p: number;
    to: number;
    turn: Turn | null;
    x: number;
    y: number;
  } | null = null;

  const pageWidth = () => book.clientWidth / (single ? 1 : 2);

  function onMove(e: PointerEvent) {
    if (!drag) {
      return;
    }
    const dx = e.clientX - drag.x;
    if (!drag.turn) {
      if (Math.abs(dx) < SLOP || Math.abs(dx) < Math.abs(e.clientY - drag.y)) {
        return;
      }
      const to = pos + (dx < 0 ? 1 : -1);
      if (to < 0 || to > last || reduced()) {
        drag = null;
        return;
      }
      settle();
      drag.from = pos;
      drag.to = to;
      drag.turn = begin(pos, to);
      live = drag.turn;
    }
    const span = pageWidth() * 1.6;
    const p = drag.turn.fwd
      ? Math.min(1, Math.max(0, -dx / span))
      : Math.min(1, Math.max(0, 1 - dx / span));
    drag.p = p;
    drag.turn.draw(p);
  }

  function onUp(e: PointerEvent) {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);
    if (!drag) {
      return;
    }
    const d = drag;
    drag = null;
    if (!d.turn) {
      if (e.type === "pointerup" && Math.abs(e.clientX - d.x) < SLOP) {
        const r = book.getBoundingClientRect();
        const forward = single || pos === 0 || e.clientX > r.left + r.width / 2;
        go(pos + (forward ? 1 : -1));
      }
      return;
    }
    const { turn } = d;
    const commit = turn.fwd ? d.p > COMMIT : d.p < 1 - COMMIT;
    if (commit) {
      pos = d.to;
      changed();
    }
    animate(turn, d.p, commit ? turn.target : turn.start, SPRING_MS, () => {
      if (live === turn) {
        live = null;
        turn.end(commit);
      }
    });
  }

  function onDown(e: PointerEvent) {
    if (e.button !== 0 || !(e.target as Element).closest(".book-flip__book")) {
      return;
    }
    drag = { from: pos, p: 0, to: pos, turn: null, x: e.clientX, y: e.clientY };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }
  stage.addEventListener("pointerdown", onDown);

  return {
    destroy() {
      live = null;
      cancelAnimationFrame(frame);
      stage.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      area.remove();
      stage.classList.remove("is-single");
    },
    go,
    jump,
    last,
    get pos() {
      return pos;
    },
    setSheets(next: FlipSheets) {
      sheets = next;
      setSlot("l", shown.l);
      setSlot("r", shown.r);
    },
    get spread() {
      return spreadOf(pos);
    },
    toPos(spread: number) {
      if (spread <= 0) {
        return 0;
      }
      if (!single) {
        return Math.min(spread, last);
      }
      const i = faces.findIndex((f) => f.s === spread);
      return i < 0 ? last : i;
    },
  };
}
